if (!customElements.get("sticky-atc-bar")) {
  customElements.define(
    "sticky-atc-bar",
    class StickyAtcBar extends HTMLElement {
      quantityAbortController = undefined;
      variantChangeUnsubscriber = undefined;

      constructor() {
        super();
        document.body.classList.add("sticky-atc-bar-enabled");

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector("quantity-input input");
      }
      get quantity() {
        return this.querySelector("quantity-input");
      }
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute("data-product-id");
      }
      get select() {
        return this.querySelector("select");
      }
      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      disconnectedCallback() {
        this.quantityAbortController?.abort();
        this.variantChangeUnsubscriber?.();
        this.observer?.disconnect();
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest(".sticky-atc-bar");
        this.submitButton = this.querySelector('[type="submit"]');
        this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;

        this.variantData = this.getVariantData();
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
        this.updatePurchaseState(this.isSelectedVariantPurchasable());

        this.init();
        this.select.addEventListener("change", (e) => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector("variant-selects");
          this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
          this.updateQuantityInput();

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll("select, fieldset"), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case "SELECT":
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll("option");
                  options.forEach((option) => option.removeAttribute("selected"));

                  element.value = variantOptionVal;
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute("selected", "selected");
                  }
                  break;
                case "FIELDSET":
                  Array.from(element.querySelectorAll("input")).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event("change", { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          if (!this.currentVariant) {
            this.updatePurchaseState(false);
          } else {
            this.updatePrice();
            this.updatePurchaseState(this.isSelectedVariantPurchasable());
          }
        });

        const hasRequiredFields = this.validateMainProductRequiredFields();
        if (hasRequiredFields && this.submitButton) {
          this.submitButton.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.productFormActions.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => {
              this.productFormActions.requestSubmit();
            }, 300);
          });
        }
      }

      validateMainProductRequiredFields() {
        const mainForm = this.mainProductInfo;
        if (!mainForm) return true;
        const requiredFields = mainForm.querySelectorAll("[data-custom-property]");
        return requiredFields.length > 0;
      }

      updateQuantityInput() {
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
        if (!this.currentVariant) return;

        const variantId = this.currentVariant.id;
        this.quantityAbortController?.abort();
        const abortController = new AbortController();
        this.quantityAbortController = abortController;

        fetch(`${this.getAttribute("data-url")}?variant=${variantId}&section_id=${this.sectionId}`, {
          signal: abortController.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Failed to update sticky quantity rules: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (
              this.quantityAbortController !== abortController ||
              String(variantId) !== String(this.selectedVariantId)
            ) {
              return;
            }
            const parsedHTML = new DOMParser().parseFromString(responseText, "text/html");
            const quantity = this.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (quantity && newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            console.error(error);
          })
          .finally(() => {
            if (this.quantityAbortController === abortController) {
              this.quantityAbortController = undefined;
            }
          });
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add("sticky-atc-bar--show");
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? "add" : "remove";
              this.container.classList[method]("sticky-atc-bar--show");
            });
          },
          { threshold: 1, rootMargin },
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        document.documentElement.style.setProperty("--sticky-atc-bar-height", this.clientHeight + "px");
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.productForm;
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector("span");
        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          addButton.setAttribute("aria-disabled", "true");
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          addButton.setAttribute("aria-disabled", "false");
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      isSelectedVariantPurchasable() {
        const selectedOption = this.select.options[this.select.selectedIndex];
        return selectedOption?.dataset.purchasable === "true";
      }

      updatePurchaseState(purchasable) {
        this.updateButton(
          !purchasable,
          purchasable ? FoxTheme.variantStrings.addToCart : FoxTheme.variantStrings.soldOut,
        );

        const dynamicCheckout = this.querySelector("[data-sticky-dynamic-checkout]");
        if (!dynamicCheckout) return;

        dynamicCheckout.hidden = !purchasable;
        dynamicCheckout.inert = !purchasable;
        dynamicCheckout.setAttribute("aria-hidden", purchasable ? "false" : "true");
        dynamicCheckout
          .querySelectorAll("button, input, select")
          .forEach((control) => (control.disabled = !purchasable));
      }

      updatePrice() {
        const classes = {
          onSale: "f-price--on-sale",
          soldOut: "f-price--sold-out",
        };
        const selectors = {
          priceWrapper: ".f-price",
          salePrice: ".f-price-item--sale",
          compareAtPrice: [".f-price-item--regular"],
          unitPriceWrapper: ".f-price__unit-wrapper",
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this,
        );
        const unitPrice = unitPriceWrapper.querySelector(".f-price__unit");

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)),
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove("hidden");
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat,
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add("hidden");
        }
      }

      syncWithMainProductForm() {
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
            if (!isMainProduct) return;
            const variant = event.data.variant;
            const variantInput = this.querySelector('[name="id"]');

            this.currentVariant = variant;
            if (!variant) {
              this.updatePurchaseState(false);
              return;
            }

            variantInput.value = variant.id;
            this.selectedVariantId = String(variant.id);
            this.updatePrice();
            this.updatePurchaseState(this.isSelectedVariantPurchasable());

            this.updateQuantityInput();
          },
        );
      }
    },
  );
}
